import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, ShoppingBag, Users, IndianRupee,
  AlertTriangle, RefreshCw, ArrowUpRight, Percent, Boxes
} from 'lucide-react';
import { analyticsAPI } from '../services/api';
import { LineChart, BarChart, ColumnChart, DonutChart } from '../components/charts/Charts';

const RANGES = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '1 year', value: 365 },
];

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const formatNumber = (value) => Number(value || 0).toLocaleString('en-IN');

const KpiCard = ({ title, value, change, icon: Icon, accent }) => {
  const positive = change >= 0;
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={`p-2.5 rounded-lg ${accent}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      {change !== undefined && change !== null && (
        <div className={`flex items-center gap-1 mt-3 text-sm ${positive ? 'text-green-600' : 'text-red-600'}`}>
          {positive ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
          <span>{Math.abs(change)}%</span>
          <span className="text-gray-400">vs prev period</span>
        </div>
      )}
    </div>
  );
};

const SectionCard = ({ title, subtitle, children, action }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200">
    <div className="flex items-center justify-between p-5 border-b border-gray-100">
      <div>
        <h3 className="font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Funnel = ({ funnel }) => {
  const steps = [
    { label: 'Page Views', value: funnel.page_views, color: 'bg-indigo-500' },
    { label: 'Product Views', value: funnel.product_views, color: 'bg-sky-500' },
    { label: 'Add to Cart', value: funnel.add_to_cart, color: 'bg-emerald-500' },
    { label: 'Checkout', value: funnel.begin_checkout, color: 'bg-amber-500' },
    { label: 'Purchases', value: funnel.purchases, color: 'bg-rose-500' },
  ];
  const max = Math.max(...steps.map((s) => s.value), 1);

  return (
    <div className="space-y-3">
      {steps.map((step, i) => {
        const pct = (step.value / max) * 100;
        const prev = i > 0 ? steps[i - 1].value : null;
        const dropRate = prev ? Math.round((step.value / prev) * 100) : null;
        return (
          <div key={step.label}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">{step.label}</span>
              <span className="font-medium text-gray-900">
                {formatNumber(step.value)}
                {dropRate !== null && <span className="text-gray-400 ml-2">({dropRate}%)</span>}
              </span>
            </div>
            <div className="bg-gray-100 rounded-full h-3 overflow-hidden">
              <div className={`h-full rounded-full ${step.color} transition-all duration-500`} style={{ width: `${Math.max(pct, 2)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Analytics = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [sales, setSales] = useState(null);
  const [products, setProducts] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [metric, setMetric] = useState('revenue');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ov, sl, pr, inv, cust] = await Promise.all([
        analyticsAPI.getOverview(days),
        analyticsAPI.getSales(days),
        analyticsAPI.getProducts(days),
        analyticsAPI.getInventory(),
        analyticsAPI.getCustomers(days),
      ]);
      setOverview(ov);
      setSales(sl);
      setProducts(pr);
      setInventory(inv);
      setCustomers(cust);
    } catch (err) {
      console.error('Analytics load error:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertTriangle className="mx-auto text-red-500 mb-3" size={32} />
        <p className="text-red-700 font-medium">{error}</p>
        <button onClick={loadData} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg text-sm">Retry</button>
      </div>
    );
  }

  const kpis = overview?.kpis || {};
  const funnel = overview?.funnel || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-500 mt-1">Business performance and insights for the last {days} days</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 rounded-lg p-1">
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setDays(r.value)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  days === r.value ? 'bg-white text-indigo-600 shadow-sm font-medium' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button onClick={loadData} className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50" title="Refresh">
            <RefreshCw size={18} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard title="Revenue" value={formatCurrency(kpis.revenue)} change={kpis.revenue_change} icon={IndianRupee} accent="bg-indigo-500" />
        <KpiCard title="Orders" value={formatNumber(kpis.orders)} change={kpis.orders_change} icon={ShoppingBag} accent="bg-sky-500" />
        <KpiCard title="Units Sold" value={formatNumber(kpis.units_sold)} change={kpis.units_change} icon={Boxes} accent="bg-emerald-500" />
        <KpiCard title="Avg Order Value" value={formatCurrency(kpis.avg_order_value)} change={kpis.aov_change} icon={ArrowUpRight} accent="bg-amber-500" />
        <KpiCard title="Customers" value={formatNumber(kpis.customers)} change={kpis.customers_change} icon={Users} accent="bg-purple-500" />
        <KpiCard title="Conversion" value={`${kpis.conversion_rate || 0}%`} icon={Percent} accent="bg-rose-500" />
      </div>

      {/* Sales trend */}
      <SectionCard
        title="Sales Trend"
        subtitle={metric === 'revenue' ? 'Daily revenue' : 'Daily orders'}
        action={
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button onClick={() => setMetric('revenue')} className={`px-3 py-1 text-sm rounded-md ${metric === 'revenue' ? 'bg-white shadow-sm font-medium text-indigo-600' : 'text-gray-600'}`}>Revenue</button>
            <button onClick={() => setMetric('orders')} className={`px-3 py-1 text-sm rounded-md ${metric === 'orders' ? 'bg-white shadow-sm font-medium text-indigo-600' : 'text-gray-600'}`}>Orders</button>
          </div>
        }
      >
        <LineChart
          data={metric === 'revenue' ? (sales?.revenue_series || []) : (sales?.orders_series || [])}
          color={metric === 'revenue' ? '#6366f1' : '#0ea5e9'}
          valuePrefix={metric === 'revenue' ? '₹' : ''}
        />
      </SectionCard>

      {/* Funnel + status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Conversion Funnel" subtitle="From browsing to purchase">
          <Funnel funnel={funnel} />
        </SectionCard>
        <SectionCard title="Order Status" subtitle="Distribution across statuses">
          <DonutChart data={(sales?.status_breakdown || []).map((s) => ({ label: s.label, value: s.value }))} />
        </SectionCard>
      </div>

      {/* Top products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Top Products by Revenue" subtitle="Click a product for detailed analysis">
          <div className="space-y-1">
            {(products?.top_by_revenue || []).slice(0, 8).map((p, i) => (
              <button
                key={p.product_id || i}
                onClick={() => p.product_id && navigate(`/analytics/product/${p.product_id}`)}
                className="w-full flex items-center justify-between py-2.5 px-2 rounded-lg hover:bg-gray-50 text-left group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-6 h-6 shrink-0 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold flex items-center justify-center">{i + 1}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate group-hover:text-indigo-600">{p.name}</p>
                    <p className="text-xs text-gray-500">{formatNumber(p.units)} units · {p.views} views</p>
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-900 shrink-0">{formatCurrency(p.revenue)}</span>
              </button>
            ))}
            {!(products?.top_by_revenue || []).length && <p className="text-sm text-gray-400 text-center py-8">No sales data yet</p>}
          </div>
        </SectionCard>

        <SectionCard title="Most Viewed Products" subtitle="Customer interest by views">
          <BarChart
            data={(products?.top_by_views || []).slice(0, 7).map((p) => ({ label: p.name, value: p.views }))}
            color="#0ea5e9"
          />
        </SectionCard>
      </div>

      {/* Inventory */}
      <SectionCard title="Inventory Health" subtitle="Stock levels and value">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Inventory Value</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(inventory?.summary?.inventory_value)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Total Units</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{formatNumber(inventory?.summary?.total_units)}</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-4">
            <p className="text-sm text-amber-700">Low Stock</p>
            <p className="text-xl font-bold text-amber-700 mt-1">{formatNumber(inventory?.summary?.low_stock_count)}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <p className="text-sm text-red-700">Out of Stock</p>
            <p className="text-xl font-bold text-red-700 mt-1">{formatNumber(inventory?.summary?.out_of_stock_count)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Stock by Size</h4>
            <ColumnChart data={inventory?.size_distribution || []} color="#22c55e" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-500" /> Restock Soon
            </h4>
            <div className="space-y-2 max-h-[220px] overflow-y-auto">
              {(inventory?.low_stock || []).map((p) => (
                <button
                  key={p.product_id}
                  onClick={() => navigate(`/analytics/product/${p.product_id}`)}
                  className="w-full flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 text-left"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                    <p className="text-xs text-gray-500">{p.collection}</p>
                  </div>
                  <span className="text-sm font-semibold text-amber-600">{p.stock} left</span>
                </button>
              ))}
              {!(inventory?.low_stock || []).length && <p className="text-sm text-gray-400 text-center py-6">All products well stocked</p>}
            </div>
          </div>
        </div>

        {(inventory?.by_collection || []).length > 0 && (
          <div className="mt-8">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Inventory Value by Collection</h4>
            <BarChart
              data={(inventory?.by_collection || []).map((c) => ({ label: c.collection, value: c.value }))}
              color="#a855f7"
              valuePrefix="₹"
            />
          </div>
        )}
      </SectionCard>

      {/* Customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Customer Mix" subtitle="New vs returning buyers">
          <DonutChart
            data={[
              { label: 'New', value: customers?.summary?.new_customers || 0 },
              { label: 'Returning', value: customers?.summary?.returning_customers || 0 },
            ]}
          />
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
            <Users size={16} /> {formatNumber(customers?.summary?.total_registered)} registered customers
          </div>
        </SectionCard>

        <SectionCard title="Top Customers" subtitle="By lifetime spend">
          <div className="space-y-1">
            {(customers?.top_customers || []).slice(0, 8).map((c, i) => (
              <div key={c.email || i} className="flex items-center justify-between py-2.5 px-2">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-6 h-6 shrink-0 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold flex items-center justify-center">{i + 1}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{c.email}</p>
                    <p className="text-xs text-gray-500">{c.orders} orders</p>
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-900 shrink-0">{formatCurrency(c.total_spent)}</span>
              </div>
            ))}
            {!(customers?.top_customers || []).length && <p className="text-sm text-gray-400 text-center py-8">No customer data yet</p>}
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default Analytics;
